import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /**
   * Track the scroll position when locking the body.
   * @type {number | null}
   */
  #lockedScrollY = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Open the native dialog first. Locking the body before `showModal()` can
    // lead to an invisible/off-screen dialog while the page is left unresponsive.
    try {
      dialog.showModal();
    } catch (error) {
      console.error(error);
      // Fail safe: never leave the page locked if opening fails.
      this.#releaseScrollLock();
      try {
        if (dialog.open) dialog.close();
      } catch {
        // ignore
      }
      return;
    }

    this.#applyScrollLock();
    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });

    // Watchdog (search only): if the dialog is open but not visible, immediately recover.
    // We intentionally scope this to search because drawers can start offscreen during animations.
    const shouldWatchdog = this.id === 'search-modal' || dialog.classList.contains('search-modal__content');
    if (shouldWatchdog) {
      requestAnimationFrame(() => {
        if (!dialog.open) return;

        const rect = dialog.getBoundingClientRect();
        const style = window.getComputedStyle(dialog);
        const opacity = Number.parseFloat(style.opacity || '1');
        const isAnimating = dialog.getAnimations({ subtree: false }).some((a) => a.playState === 'running');

        const hasSize = rect.width > 0 && rect.height > 0;
        // The search dialog animates in from opacity:0, so treat opacity=0 as OK while animating.
        const opacityOk = opacity > 0.01 || isAnimating;

        const isRenderable = style.display !== 'none' && style.visibility !== 'hidden' && opacityOk;

        // Slide-in animations (e.g. translateX(-100%)) place the dialog off-screen initially,
        // so skip the viewport intersection check while any animation is running.
        const intersectsViewport =
          isAnimating || (rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight);

        if (!(hasSize && isRenderable && intersectsViewport)) {
          this.closeDialog();
        }
      });
    }
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    this.#releaseScrollLock();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Declarative handler used by dialogs that set `on:click="/closeDialogOnClickOutside"`.
   * @param {MouseEvent} event
   */
  closeDialogOnClickOutside(event) {
    this.#handleClick(event);
  }

  /**
   * Declarative handler used by dialogs that set `on:keydown="/closeDialogOnEscapePress"`.
   * @param {KeyboardEvent} event
   */
  closeDialogOnEscapePress(event) {
    this.#handleKeyDown(event);
  }

  #applyScrollLock() {
    if (this.#lockedScrollY !== null) return;

    this.#lockedScrollY = window.scrollY;

    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#lockedScrollY}px`;
  }

  #releaseScrollLock() {
    // Safety net: ensure html scroll-lock cannot stick.
    document.documentElement.removeAttribute('scroll-lock');

    document.body.style.width = '';

    const top = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';

    const scrollY =
      this.#lockedScrollY !== null
        ? this.#lockedScrollY
        : top
          ? Math.abs(Number.parseInt(top, 10))
          : null;

    this.#lockedScrollY = null;

    if (typeof scrollY === 'number' && !Number.isNaN(scrollY)) {
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    }
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
